import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { http, fetchBrand } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { useI18n } from "@/lib/i18n";

export default function BrandClaim() {
  const { slug } = useParams();
  const { locale } = useI18n();
  const [brand, setBrand] = useState(null);
  const [f, setF] = useState({ work_email: "", full_name: "", role_at_brand: "", note: "" });
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => { fetchBrand(slug).then((d) => setBrand(d.brand)).catch(() => {}); }, [slug]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await http.post("/brand/claims", { brand_slug: slug, ...f });
      setDone(true); toast.success("Claim submitted for review");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!brand) return <div className="bp-container py-24" />;

  return (
    <>
      <Seo title={`Claim ${brand.display_name}`} description="Claim this brand profile — free." canonicalPath={`/brands/${slug}/claim`} locale={locale} />
      <div className="bp-container py-16 max-w-2xl">
        <Link to={`/brands/${slug}`} className="text-xs uppercase tracking-widest bp-link" data-testid="claim-back">← Back to profile</Link>
        <h1 className="font-serif text-4xl md:text-5xl mt-4 mb-2">Is this your brand?</h1>
        <p className="mb-6" style={{ color: "var(--bp-text-secondary)" }}>
          Claim the transparency profile for <b>{brand.display_name}</b>. Claiming is free and does not change any status on the profile — it grants you access to a lightweight brand dashboard where you can submit public links we may have missed.
        </p>
        {done ? (
          <div className="p-6 bg-white border" style={{ borderColor: "var(--bp-border)" }} data-testid="claim-thanks">
            <p className="font-serif text-xl mb-2">Thanks — your claim is pending review.</p>
            <p className="text-sm" style={{ color: "var(--bp-text-secondary)" }}>An editor will verify your identity and email you a sign-in link. This typically takes 3–5 business days.</p>
            <Button onClick={() => nav(`/brands/${slug}`)} className="rounded-none mt-4" data-testid="claim-back-profile">Back to profile</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 p-6 bg-white border" style={{ borderColor: "var(--bp-border)" }}>
            <div>
              <label className="text-xs uppercase tracking-widest">Full name</label>
              <Input required value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} data-testid="claim-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest">Work email on the brand&apos;s domain</label>
              <Input required type="email" value={f.work_email} onChange={(e) => setF({ ...f, work_email: e.target.value })} data-testid="claim-email" placeholder={`you@${brand.website_url?.replace(/https?:\/\//, "").replace(/\/$/, "").replace(/^www\./, "") || "brand.com"}`} />
              <p className="text-xs mt-1" style={{ color: "var(--bp-text-muted)" }}>We use the email domain as one signal to verify your role. We may ask for additional evidence.</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest">Your role at the brand</label>
              <Input required value={f.role_at_brand} onChange={(e) => setF({ ...f, role_at_brand: e.target.value })} data-testid="claim-role" placeholder="Head of Sustainability" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest">Note to editors (optional)</label>
              <Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="claim-note" />
            </div>
            <p className="text-xs italic" style={{ color: "var(--bp-text-muted)" }}>
              Claiming is free and does not change the transparency profile. It gives you access to a dashboard to submit public links.
            </p>
            <Button type="submit" disabled={busy} className="rounded-none w-full" data-testid="claim-submit">{busy ? "Submitting…" : "Submit claim"}</Button>
          </form>
        )}
      </div>
    </>
  );
}
